// Session memory: keeps the whole conversation as an in-memory, punctuation-segmented
// "session markdown" so that content dropped by context compression can still be
// retrieved (auto pre-retrieval + session_memory_search / session_memory_read tools).

const SEGMENT_TARGET = 300;
const SEGMENT_HARD_MAX = 450;
const SNIPPET_MAX = 500;
const READ_MAX_CHARS = 8000;
const READ_MAX_MESSAGES = 20;
const ATTACHMENT_READ_PREVIEW = 1500;
const REGEX_MAX_LENGTH = 200;
const BM25_K1 = 1.2;
const BM25_B = 0.75;

const SENTENCE_BREAKS = new Set(['。', '！', '？', '；', '!', '?', ';', '\n']);
const TOKEN_REGEX = /[㐀-䶿一-鿿぀-ヿ가-힯]+|[a-z0-9_]+(?:[.\-:/][a-z0-9_]+)*/g;
const CJK_RUN_REGEX = /^[㐀-䶿一-鿿぀-ヿ가-힯]/;

let cache = { history: null, signature: '', memory: null };

function normalizeText(text) {
  return String(text || '')
    .replace(/data:[a-z]+\/[a-z0-9.+-]+;base64,[A-Za-z0-9+/=]+/gi, '[binary]')
    .replace(/\r\n?/g, '\n');
}

// Split on Chinese/English sentence punctuation and newlines. "." only counts
// when followed by whitespace so decimals, URLs and file names stay intact.
function splitSentences(text) {
  const sentences = [];
  let current = '';
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    current += ch;
    const isBreak = SENTENCE_BREAKS.has(ch) || (ch === '.' && /\s/.test(text[i + 1] || ' '));
    if (isBreak) {
      if (current.trim()) sentences.push(current);
      current = '';
    }
  }
  if (current.trim()) sentences.push(current);
  return sentences;
}

function segmentText(text) {
  const segments = [];
  let buffer = '';
  const flush = () => {
    const trimmed = buffer.trim();
    if (trimmed) segments.push(trimmed);
    buffer = '';
  };
  splitSentences(text).forEach(sentence => {
    if (sentence.length > SEGMENT_HARD_MAX) {
      flush();
      for (let i = 0; i < sentence.length; i += SEGMENT_TARGET) {
        buffer = sentence.slice(i, i + SEGMENT_TARGET);
        flush();
      }
      return;
    }
    if (buffer.length + sentence.length > SEGMENT_TARGET) flush();
    buffer += sentence;
  });
  flush();
  return segments;
}

export function tokenize(text) {
  const tokens = [];
  const lower = String(text || '').toLowerCase();
  const runs = lower.match(TOKEN_REGEX) || [];
  runs.forEach(run => {
    if (CJK_RUN_REGEX.test(run)) {
      if (run.length === 1) {
        tokens.push(run);
        return;
      }
      for (let i = 0; i < run.length - 1; i += 1) tokens.push(run.slice(i, i + 2));
      return;
    }
    tokens.push(run);
    const parts = run.split(/[.\-:/_]/).filter(part => part && part !== run);
    if (parts.length > 1) parts.forEach(part => tokens.push(part));
  });
  return tokens;
}

function roleLabel(role) {
  if (role === 'user') return 'User';
  if (role === 'assistant') return 'AI';
  return role || 'Unknown';
}

function historySignature(history) {
  const last = history[history.length - 1];
  const lastLen = last && typeof last.content === 'string' ? last.content.length : 0;
  return `${history.length}:${lastLen}`;
}

export function buildMemory(history = []) {
  const list = Array.isArray(history) ? history : [];
  const signature = historySignature(list);
  if (cache.history === list && cache.signature === signature && cache.memory) {
    return cache.memory;
  }

  const messages = [];
  const segments = [];
  list.forEach((message, msgIndex) => {
    if (!message || message.role === 'system') return;
    const content = normalizeText(message.content);
    const attachments = Array.isArray(message.attachments)
      ? message.attachments
          .filter(att => att && att.type === 'file' && att.content)
          .map(att => ({ name: att.name || 'file', content: normalizeText(att.content) }))
      : [];
    messages.push({ msgIndex, role: message.role, content, attachments });

    const pushSegments = (text, attachmentName) => {
      segmentText(text).forEach((segmentTextValue, order) => {
        const tokens = tokenize(segmentTextValue);
        const tf = new Map();
        tokens.forEach(token => tf.set(token, (tf.get(token) || 0) + 1));
        segments.push({
          id: segments.length,
          msgIndex,
          role: message.role,
          order,
          attachmentName: attachmentName || '',
          text: segmentTextValue,
          lower: segmentTextValue.toLowerCase(),
          length: tokens.length || 1,
          tf
        });
      });
    };
    pushSegments(content, '');
    attachments.forEach(att => pushSegments(att.content, att.name));
  });

  const df = new Map();
  let totalLength = 0;
  segments.forEach(segment => {
    totalLength += segment.length;
    segment.tf.forEach((_, token) => df.set(token, (df.get(token) || 0) + 1));
  });

  const memory = {
    messages,
    segments,
    df,
    avgLength: segments.length ? totalLength / segments.length : 1
  };
  cache = { history: list, signature, memory };
  return memory;
}

export function getCompressedBoundary(history = []) {
  const list = Array.isArray(history) ? history : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i] && list[i].summaryInfo) return i;
  }
  return -1;
}

function parseRegexQuery(query) {
  const match = /^\/(.+)\/([gimsuy]*)$/s.exec(query);
  if (!match) return null;
  if (match[1].length > REGEX_MAX_LENGTH) throw new Error('正则表达式过长。');
  // Drop "g" so RegExp#test stays stateless; search is case-insensitive by default.
  let flags = match[2].replace(/g/g, '');
  if (!flags.includes('i')) flags += 'i';
  return new RegExp(match[1], flags);
}

function scoreSegments(memory, query, candidates) {
  const queryTokens = [...new Set(tokenize(query))];
  const normalizedQuery = String(query).trim().toLowerCase();
  const total = memory.segments.length || 1;
  return candidates.map(segment => {
    let score = 0;
    queryTokens.forEach(token => {
      const freq = segment.tf.get(token);
      if (!freq) return;
      const docFreq = memory.df.get(token) || 0;
      const idf = Math.log(1 + (total - docFreq + 0.5) / (docFreq + 0.5));
      const norm = freq + BM25_K1 * (1 - BM25_B + BM25_B * (segment.length / memory.avgLength));
      score += idf * ((freq * (BM25_K1 + 1)) / norm);
    });
    if (normalizedQuery.length >= 2 && segment.lower.includes(normalizedQuery)) {
      score += 3 + Math.min(normalizedQuery.length / 10, 3);
    }
    return { segment, score };
  });
}

// Merge hits from the same message/attachment that sit next to each other.
function mergeHits(hits) {
  const sorted = [...hits].sort((a, b) => a.segment.id - b.segment.id);
  const merged = [];
  sorted.forEach(hit => {
    const prev = merged[merged.length - 1];
    if (
      prev
      && prev.msgIndex === hit.segment.msgIndex
      && prev.attachmentName === hit.segment.attachmentName
      && prev.lastId + 1 === hit.segment.id
      && prev.text.length + hit.segment.text.length <= SNIPPET_MAX
    ) {
      prev.text += hit.segment.text;
      prev.lastId = hit.segment.id;
      prev.score = Math.max(prev.score, hit.score);
      return;
    }
    merged.push({
      msgIndex: hit.segment.msgIndex,
      role: hit.segment.role,
      attachmentName: hit.segment.attachmentName,
      text: hit.segment.text,
      lastId: hit.segment.id,
      score: hit.score
    });
  });
  return merged;
}

/**
 * Search the session memory.
 * @param {object} memory  result of buildMemory()
 * @param {string} query   keywords, a phrase, or /regex/
 * @param {object} options topK, maxIndex (only messages with index <= maxIndex),
 *                         minScore (absolute floor for BM25 hits)
 */
export function search(memory, query, { topK = 5, maxIndex = Infinity, minScore = 0 } = {}) {
  const text = String(query || '').trim();
  if (!text) return [];
  const limit = Math.max(1, Math.min(Number(topK) || 5, 20));
  const candidates = memory.segments.filter(segment => segment.msgIndex <= maxIndex);

  const regex = parseRegexQuery(text);
  let hits;
  if (regex) {
    hits = candidates
      .filter(segment => regex.test(segment.text))
      .map(segment => ({ segment, score: 1 }))
      .slice(-limit);
  } else {
    const scored = scoreSegments(memory, text, candidates).filter(hit => hit.score > 0);
    if (!scored.length) return [];
    const best = Math.max(...scored.map(hit => hit.score));
    hits = scored
      .filter(hit => hit.score >= minScore && hit.score >= best * 0.3)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);
  }

  return mergeHits(hits)
    .sort((a, b) => b.score - a.score || a.msgIndex - b.msgIndex)
    .map(hit => ({
      msg: hit.msgIndex,
      role: roleLabel(hit.role),
      attachment: hit.attachmentName || undefined,
      text: hit.text.slice(0, SNIPPET_MAX),
      score: Math.round(hit.score * 100) / 100
    }));
}

export function toMarkdown(memory, from = 0, to = Infinity) {
  return memory.messages
    .filter(message => message.msgIndex >= from && message.msgIndex <= to)
    .map(message => {
      let block = `## #${message.msgIndex} ${roleLabel(message.role)}\n${message.content}`;
      message.attachments.forEach(att => {
        const preview = att.content.length > ATTACHMENT_READ_PREVIEW
          ? `${att.content.slice(0, ATTACHMENT_READ_PREVIEW)}\n...（附件共 ${att.content.length} 字，其余部分可用 session_memory_search 检索）`
          : att.content;
        block += `\n\n### 附件：${att.name}\n${preview}`;
      });
      return block;
    })
    .join('\n\n');
}

export function read(memory, { from, to } = {}) {
  const start = Math.max(0, Math.floor(Number(from) || 0));
  let end = Number.isFinite(Number(to)) ? Math.floor(Number(to)) : start;
  if (end < start) end = start;
  if (end - start + 1 > READ_MAX_MESSAGES) end = start + READ_MAX_MESSAGES - 1;
  const markdown = toMarkdown(memory, start, end);
  const truncated = markdown.length > READ_MAX_CHARS;
  return {
    from: start,
    to: end,
    content: truncated ? `${markdown.slice(0, READ_MAX_CHARS)}\n...（已截断，请缩小范围继续读取）` : markdown,
    truncated
  };
}

/**
 * Run a session_memory_* tool call against the given history and return the
 * JSON string handed back to the model.
 */
export function executeSessionMemoryTool(name, args = {}, history = []) {
  const memory = buildMemory(history);
  const boundary = getCompressedBoundary(history);
  if (name === 'session_memory_search') {
    if (!args.query) throw new Error('搜索词不能为空。');
    const results = search(memory, args.query, {
      topK: args.top_k || 8,
      maxIndex: boundary >= 0 ? boundary : Infinity
    });
    return JSON.stringify({ query: args.query, compressed_until: boundary, results });
  }
  if (name === 'session_memory_read') {
    return JSON.stringify(read(memory, args));
  }
  throw new Error(`未知的会话记忆工具：${name}`);
}

/**
 * Build the auto pre-retrieval note injected after the summary.
 * Returns '' when nothing relevant is found.
 */
export function buildRecallNote(history, query, { topK = 5, maxChars = 3000 } = {}) {
  const boundary = getCompressedBoundary(history);
  if (boundary < 0 || !query) return '';
  let results;
  try {
    results = search(buildMemory(history), query, { topK, maxIndex: boundary, minScore: 1.5 });
  } catch (_) {
    return '';
  }
  if (!results.length) return '';
  let used = 0;
  const lines = [];
  for (const result of results) {
    const label = `#${result.msg} ${result.role}${result.attachment ? `（附件 ${result.attachment}）` : ''}`;
    const line = `- ${label}: ${result.text}`;
    if (used + line.length > maxChars) break;
    lines.push(line);
    used += line.length;
  }
  if (!lines.length) return '';
  return `[System Note: 以下为与当前问题相关的早期对话原文片段（#编号为消息序号），如需更多细节可调用 session_memory_search / session_memory_read]\n${lines.join('\n')}`;
}

export const SESSION_MEMORY_TOOL_NAMES = ['session_memory_search', 'session_memory_read'];
